import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as Email } from "@assets/icons/email.svg";
import { ReactComponent as Linkedin } from "@assets/icons/linkedin.svg";

import { SvgIcon } from "@/components/Icon/SvgIcon";

import { CompanySeatSummary, SeatKindSummary } from "../schema/companies";

/** The green "+N bonus" pill; hidden when there is nothing to show. */
export const BonusBadge: FunctionComponent<{ amount: number }> = ({ amount }) => {
	const { t } = useTranslation();

	if (amount <= 0) {
		return null;
	}

	return (
		<span
			className="whitespace-nowrap rounded-full bg-status-success-light px-2 py-0.5 text-xs font-normal text-status-success"
			data-test="bonus-badge"
		>
			{t("companies.licences.bonusBadge", { amount })}
		</span>
	);
};

const SeatKindLine: FunctionComponent<{
	icon: typeof Linkedin;
	label: string;
	kind: SeatKindSummary;
	testId: string;
}> = ({ icon, label, kind, testId }) => (
	<div className="flex items-center gap-2" data-test={testId} title={label}>
		<SvgIcon className="h-[16px] w-[16px] shrink-0" svgIcon={icon} />
		<span className="whitespace-nowrap">
			{kind.used} / {kind.total}
		</span>
		<BonusBadge amount={kind.bonus} />
	</div>
);

/**
 * The Licences column of the admin Companies table (PREC-4063): LinkedIn and email seats as
 * `used / total`, each with the bonus pill when an admin has granted free seats.
 */
export const LicencesCell: FunctionComponent<{ seats?: CompanySeatSummary }> = ({ seats }) => {
	const { t } = useTranslation();

	if (!seats) {
		return <span className="text-text-meta">—</span>;
	}

	return (
		<div className="flex flex-col gap-1 text-sm text-text">
			<SeatKindLine
				icon={Linkedin}
				kind={seats.linkedin}
				label={t("companies.licences.linkedin")}
				testId="licences-linkedin"
			/>
			<SeatKindLine
				icon={Email}
				kind={seats.email}
				label={t("companies.licences.email")}
				testId="licences-email"
			/>
		</div>
	);
};
